import type { CaptureResult } from 'posthog-js';
import { getPerfArmProperties } from './perfArms';
import { takePerfCounters } from './perfCounters.store';

type tRoute = '/workspace/chat' | '/workspace/funnel' | undefined;
type tSentryMode = 'session' | 'buffer' | undefined;
const routes: { time: number; route: tRoute }[] = [];
let sentryReplayMode = (): tSentryMode => undefined;
let posthogRecording = () => false;
let domSnapshot: { perf_dom_nodes: number; perf_stylesheet_rules: number; perf_loaded_script_versions: string[] } | undefined;
let domReadAt = -Infinity;

export const setSentryReplayModeReader = (reader: () => tSentryMode): void => { sentryReplayMode = reader; };
export const setPosthogRecordingReader = (reader: () => boolean): void => { posthogRecording = reader; };

export const setPerfRoute = (pathname: string): void => {
  const route = pathname === '/workspace/chat' || pathname === '/workspace/funnel' ? pathname : undefined;
  if (routes.length && routes[routes.length - 1].route === route) return;
  routes.push({ time: performance.now(), route });
  if (routes.length > 128) routes.shift();
};

export const getPerfRouteAt = (time: number): tRoute => {
  for (let index = routes.length - 1; index >= 0; index -= 1) {
    if (routes[index].time <= time) return routes[index].route;
  }
  return undefined;
};

const readDom = (now: number) => {
  if (domSnapshot && now - domReadAt < 60000) return domSnapshot;
  let rules = 0;
  for (const sheet of document.styleSheets) {
    try { rules += sheet.cssRules.length; } catch { /* Cross-origin sheets are intentionally opaque. */ }
  }
  const versions = new Set<string>();
  for (const script of document.scripts) {
    const source = script.src;
    if (!source.includes('recorder') && !source.includes('web-vitals')) continue;
    let url: URL;
    try { url = new URL(source); } catch { continue; }
    const name = url.pathname.split('/').pop() ?? '';
    if (!/^(?:posthog-recorder|recorder|web-vitals)\.js$/.test(name)) continue;
    // Keep only version-shaped data, never a whole script URL or arbitrary query.
    const version = url.searchParams.get('v');
    versions.add(version && /^\d+\.\d+\.\d+[\w.-]*$/.test(version) ? `${name}@${version}` : name);
  }
  domSnapshot = { perf_dom_nodes: document.getElementsByTagName('*').length, perf_stylesheet_rules: rules, perf_loaded_script_versions: [...versions].slice(0, 10) };
  domReadAt = now;
  return domSnapshot;
};

const readContext = (options: { now: number; measuredAt: number; isPosthogRecording: () => boolean }) => {
  const { now, measuredAt, isPosthogRecording } = options;
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming & { activationStart?: number } | undefined;
  const memory = (performance as Performance & { memory?: { usedJSHeapSize: number; totalJSHeapSize: number } }).memory;
  const recorderRunning = isPosthogRecording();
  const sentryMode = sentryReplayMode();
  return {
    ...getPerfArmProperties(),
    ...readDom(now),
    perf_context_time_ms: now,
    perf_tab_age_ms: Math.max(0, measuredAt - (navigation?.activationStart ?? 0)),
    perf_build_sha: import.meta.env.VITE_BUILD_SHA ?? 'local',
    ...(memory && Number.isFinite(memory.usedJSHeapSize) ? {
      perf_js_heap_used_bytes: memory.usedJSHeapSize,
      perf_js_heap_total_bytes: memory.totalJSHeapSize,
    } : {}),
    perf_posthog_recorder_running: recorderRunning,
    perf_sentry_replay_mode: sentryMode ?? 'off',
    perf_recorder_running: recorderRunning || sentryMode !== undefined,
  };
};

export const readPerfInteractionContext = (interactionTime: number): Record<string, unknown> | undefined => {
  let snapshot: Record<string, unknown> | undefined;
  try {
    const now = performance.now();
    // Even a failed optional read marks when the snapshot was attempted, so
    // before_send cannot replace it with state from a later recording session.
    snapshot = { perf_context_time_ms: now };
    const measuredAt = Math.min(now, Math.max(0, interactionTime));
    return { ...readContext({ now, measuredAt, isPosthogRecording: posthogRecording }), ...takePerfCounters('slow') };
  } catch { return snapshot; }
};

export const enrichPerfEvent = (event: CaptureResult, isPosthogRecording: () => boolean): CaptureResult => {
  try {
    if (event.event !== '$web_vitals' && !event.event.startsWith('front.perf.')) return event;
    const slow = event.event === 'front.perf.interaction.slow';
    if (slow && typeof event.properties.perf_context_time_ms === 'number') return event;
    const now = performance.now();
    const interactionTime = slow ? event.properties.interaction_start_time_ms : undefined;
    const measuredAt = typeof interactionTime === 'number' && Number.isFinite(interactionTime) && interactionTime >= 0 && interactionTime <= now ? interactionTime : now;
    const interactionCount = slow ? event.properties.perf_interactions_total : undefined;
    const properties = { ...event.properties, ...readContext({ now, measuredAt, isPosthogRecording }) };
    // Drain only once all fallible reads have succeeded.
    return { ...event, properties: {
      ...properties, ...takePerfCounters(slow ? 'slow' : event.event === '$web_vitals' ? 'web_vitals' : 'other_perf'),
      ...(typeof interactionCount === 'number' && Number.isFinite(interactionCount) && interactionCount >= 0 ? { perf_interactions_total: interactionCount } : {}),
    } };
  } catch {
    return event;
  }
};
